// Sidebar.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation }          from 'react-router-dom';
import { auth, db }                   from '../firebase/firebase';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged }         from 'firebase/auth';
import NewProjectModal                from './NewProjectModal';
import EditProjectModal               from './EditProjectModal';
import collaborationService           from '../services/collaborationService';
import projectClosureService          from '../services/projectClosureService';
import { AlertTriangle, Users, Shield, Pencil, FileText, Tablet } from 'lucide-react';
import IpadSetupModal from './IpadSetupModal';
import { useBranding } from '../contexts/BrandingContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  getSidebarLogoDimensions,
  getSidebarLogoAlign,
  getSidebarLogoFrame,
  sidebarLogoAlignToFlexJustify,
} from '../utils/branding';
import BrandedSidebarLogo from './BrandedSidebarLogo';
import { normalizeCompanyIdForStorage } from '../utils/companyBrandingUpload';

export default function Sidebar() {
  const location = useLocation();
  const latestFetchRef = useRef(0);
  const [displayName, setDisplayName] = useState('');
  const [yourProjects, setYourProjects]       = useState([]);
  const [collaborativeProjects, setCollaborativeProjects] = useState([]);
  const [allPastProjects, setAllPastProjects] = useState([]); // Combined past projects
  const [companyProjects, setCompanyProjects] = useState([]); // View-only: other projects in the company (account owners only)
  const [isModalOpen, setIsModalOpen]         = useState(false);
  const [projectToEdit, setProjectToEdit]     = useState(null);
  const [actionPanel, setActionPanel]         = useState(null); // { project, type: 'owned'|'collab' }
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');
  const [showCloseProjectWarning, setShowCloseProjectWarning] = useState(false);
  const [projectToClose, setProjectToClose]   = useState(null);
  const [projectTeams, setProjectTeams]       = useState({}); // Store teams for each project
  const [showFinishTeamWarning, setShowFinishTeamWarning] = useState(false);
  const [teamToFinish, setTeamToFinish]       = useState(null);
  const [projectCollaborators, setProjectCollaborators] = useState({}); // Store collaborators for each project
  const [, setPendingInvitesCount] = useState(0);
  const [closingProject, setClosingProject] = useState(false); // Loading state for project closure
  const [iPadSetupProject, setIPadSetupProject] = useState(null);

  const { company: brandingCompany } = useBranding();
  const { isDark } = useTheme();
  const sidebarLogoUrl =
    typeof brandingCompany?.branding?.logoUrl === 'string' && brandingCompany.branding.logoUrl.trim()
      ? brandingCompany.branding.logoUrl.trim()
      : null;
  const sidebarLogoDims = getSidebarLogoDimensions(brandingCompany);
  const sidebarLogoAlign = getSidebarLogoAlign(brandingCompany);
  const sidebarLogoJustify = sidebarLogoAlignToFlexJustify(sidebarLogoAlign);
  const sidebarLogoFrame = getSidebarLogoFrame(brandingCompany);

  // Helper function to check if user is a collaborator on a project (both full and view-only)
  // const isUserCollaborator = (projectId, userId) => {
  //   const collaborators = projectCollaborators[projectId] || [];
  //   return collaborators.some(collab => 
  //     collab.userId === userId && collab.status === 'accepted'
  //   );
  // };

  // Helper function to get user's role on a project
  const getUserProjectRole = (project, userId) => {
    // First try to get from project document's collaborators object
    const collaborators = project.collaborators || {};
    const userCollab = collaborators[userId];
    if (userCollab && userCollab.status === 'accepted') {
      return userCollab.role;
    }
    
    // Fallback to projectCollaborators state (for backward compatibility)
    const collaboratorsList = projectCollaborators[project.id] || [];
    const collaborator = collaboratorsList.find(collab => 
      collab.userId === userId && collab.status === 'accepted'
    );
    return collaborator ? collaborator.role : null;
  };

  const resetSidebarState = useCallback(() => {
    setDisplayName('');
    setYourProjects([]);
    setCollaborativeProjects([]);
    setAllPastProjects([]);
    setCompanyProjects([]);
    setProjectTeams({});
    setProjectCollaborators({});
    setPendingInvitesCount(0);
    setActionPanel(null);
    setError('');
  }, []);

  const fetchUserAndProjects = useCallback(async (user) => {
    const fetchId = ++latestFetchRef.current;
    setLoading(true);
    setError('');
    resetSidebarState();
    try {
      console.log('Fetching data for user:', user.email);

      // Always resolve company from the authenticated user's Firestore profile.
      // Reusing a cached companyId across sessions can leak the previous account's sidebar data.
      console.log('Fetching companyId from Firestore for current user...');
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      console.log('User query result:', userDocSnap.exists() ? 'User found' : 'No user found');

      if (!userDocSnap.exists()) {
        console.log('No user record found in Firestore');
        localStorage.removeItem('companyId');
        setError('No user record found. Please contact support.');
        setLoading(false);
        return;
      }

      const data = userDocSnap.data();

      // Read the name from Firestore (kept in sync by the profile editor) rather than
      // Firebase Auth's displayName, which is only ever set once at signup and never
      // updated again — using it here meant name changes never showed up in the sidebar.
      const fallbackFirstName = (user.displayName || 'User').split(' ')[0];
      setDisplayName(data.firstName || fallbackFirstName);

      const companyId = normalizeCompanyIdForStorage(data.companyId);
      console.log('CompanyId from Firestore:', companyId);
      console.log('Full user data:', data);

      if (companyId) {
        localStorage.setItem('companyId', companyId);
      } else {
        localStorage.removeItem('companyId');
      }

      if (!companyId) {
        console.log('No companyId available');
        setError('No company ID found. Please contact support.');
        setLoading(false);
        return;
      }

      // Determine if this user is a company account owner (sees all company projects, view-only)
      let isAccountOwner = false;
      try {
        const companyDocSnap = await getDoc(doc(db, 'companies', companyId));
        if (companyDocSnap.exists()) {
          const companyData = companyDocSnap.data();
          const ownerIds = Array.isArray(companyData.accountOwnerIds)
            ? companyData.accountOwnerIds
            : (companyData.accountOwnerId ? [companyData.accountOwnerId] : []);
          isAccountOwner = ownerIds.includes(user.uid);
        }
      } catch (companyErr) {
        console.warn('⚠️ Could not fetch company doc to check account owner status:', companyErr);
      }

      // Fetch projects for this company
      // With strict security rules, Firestore automatically filters to only return:
      // 1. Projects where user is owner (createdBy)
      // 2. Projects where user is accepted collaborator
      // 3. Projects where user is member (in joinedProjects)
      console.log('Fetching accessible projects for companyId:', companyId);
      const projectsQuery = query(collection(db, 'projects'), where('companyId', '==', companyId));
      
      let projectsSnap;
      try {
        projectsSnap = await getDocs(projectsQuery);
        console.log('✅ Projects query successful, found:', projectsSnap.docs.length);
      } catch (queryError) {
        console.error('❌ Projects query failed:', queryError);
        throw new Error(`Failed to fetch projects: ${queryError.message}`);
      }

      if (fetchId !== latestFetchRef.current || auth.currentUser?.uid !== user.uid) {
        console.log('Ignoring stale sidebar fetch result for user:', user.email);
        return;
      }
      
      console.log('All company projects returned:', projectsSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
      
      // IMPORTANT: With list permission, query returns ALL projects in company
      // We must filter client-side to only show projects user has access to
      const allCompanyProjects = projectsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const projectsList = allCompanyProjects.filter(project => {
        // User has access if they are:
        // 1. Project owner
        const isOwner = project.createdBy === user.uid;

        // 2. Accepted collaborator
        const collaborators = project.collaborators || {};
        const userCollab = collaborators[user.uid];
        const isCollaborator = userCollab && userCollab.status === 'accepted';

        // 3. Project member (in joinedProjects) - for subs who might access web
        // Note: This is rare since subs use mobile, but we support it

        return isOwner || isCollaborator;
      });

      console.log('Filtered to accessible projects:', projectsList.length, 'out of', projectsSnap.docs.length);

      // Categorize projects based on ownership and collaboration
      // Helper to check if user is an accepted collaborator directly from project data
      const isAcceptedCollaborator = (project, userId) => {
        const collaborators = project.collaborators || {};
        const userCollab = collaborators[userId];
        return userCollab && userCollab.status === 'accepted';
      };
      
      // With strict rules, Firestore already filtered to accessible projects only
      // We just need to categorize them by ownership and status
      const your = projectsList.filter(p => p.createdBy === user.uid && p.status !== 'closed');
      const collaborative = projectsList.filter(p => 
        p.createdBy !== user.uid && 
        p.status !== 'closed' && 
        isAcceptedCollaborator(p, user.uid)
      );
      
      // Combine all past projects (owned + collaborated)
      const allPast = projectsList.filter(p => 
        p.status === 'closed' && 
        (p.createdBy === user.uid || isAcceptedCollaborator(p, user.uid))
      );

      // Company account owners see every other active project in the company, view-only,
      // even without an invite/collaborator status. Editing still requires an accepted
      // collaborator invite - enforced server-side by canEditProject in firestore.rules.
      const companyOnly = isAccountOwner
        ? allCompanyProjects.filter(p =>
            p.status !== 'closed' &&
            p.createdBy !== user.uid &&
            !isAcceptedCollaborator(p, user.uid)
          )
        : [];

      console.log('Your projects:', your.length);
      console.log('Collaborative projects:', collaborative.length);
      console.log('All past projects:', allPast.length);
      console.log('Company projects (view-only):', companyOnly.length);
      console.log('Your projects data:', your);
      console.log('Collaborative projects data:', collaborative);

      setYourProjects(your);
      setCollaborativeProjects(collaborative);
      setAllPastProjects(allPast);
      setCompanyProjects(companyOnly);

      // Fetch teams and collaborators for all projects
      // With strict rules, only fetch for projects we have access to
      const teamsData = {};
      const collaboratorsData = {};
      for (const project of projectsList) {
        // Skip if project is invalid or doesn't have required fields
        if (!project || !project.id) {
          console.warn('Skipping invalid project:', project);
          continue;
        }
        
        try {
          // Fetch teams (will succeed if user has project access)
          const teamsQuery = query(collection(db, `projects/${project.id}/teams`));
          const teamsSnap = await getDocs(teamsQuery);
          teamsData[project.id] = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // Fetch collaborators (will succeed if user has project access)
          const collaborators = await collaborationService.getProjectCollaborators(project.id);
          collaboratorsData[project.id] = collaborators;
        } catch (err) {
          console.warn(`⚠️ Could not fetch teams/collaborators for project ${project.id}:`, err);
          // Set empty arrays for projects we can't access (shouldn't happen with filtered query)
          teamsData[project.id] = [];
          collaboratorsData[project.id] = [];
        }
      }
      setProjectTeams(teamsData);
      setProjectCollaborators(collaboratorsData);

      // Fetch pending invites count
      const invites = await collaborationService.getPendingInvitesForUser();
      setPendingInvitesCount(invites.length);
    } catch (err) {
      console.error('Error fetching user/projects:', err);
      console.error('Error details:', err.message, err.code);
      
      // Provide more helpful error message
      if (err.code === 'permission-denied') {
        setError('Permission denied. Some projects may have been deleted or you may need to refresh your session.');
      } else {
        setError('Error loading projects. Please refresh the page or sign out and back in.');
      }
    }
    setLoading(false);
  }, [resetSidebarState]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('User authenticated:', user.email);
        fetchUserAndProjects(user);
      } else {
        console.log('No user authenticated');
        latestFetchRef.current += 1;
        localStorage.removeItem('companyId');
        resetSidebarState();
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [fetchUserAndProjects, resetSidebarState]);

  // Listen for project invitation events to refresh sidebar
  useEffect(() => {
    const handleInvitationAccepted = (event) => {
      console.log('🔔 Project invitation accepted, refreshing sidebar...', event.detail);
      const user = auth.currentUser;
      if (user) {
        fetchUserAndProjects(user);
      }
    };

    const handleInvitationDeclined = (event) => {
      console.log('🔔 Project invitation declined, refreshing sidebar...', event.detail);
      const user = auth.currentUser;
      if (user) {
        // Refresh to update pending invites count
        fetchUserAndProjects(user);
      }
    };

    window.addEventListener('projectInvitationAccepted', handleInvitationAccepted);
    window.addEventListener('projectInvitationDeclined', handleInvitationDeclined);

    return () => {
      window.removeEventListener('projectInvitationAccepted', handleInvitationAccepted);
      window.removeEventListener('projectInvitationDeclined', handleInvitationDeclined);
    };
  }, [fetchUserAndProjects]);

  // Close dropdown when clicking outside
  useEffect(() => {
      const handleClickOutside = () => setActionPanel(null);

    if (actionPanel) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [actionPanel]);

  // Function to mark team as finished
  const handleMarkTeamFinished = async (projectId, teamId) => {
    try {
      const teamRef = doc(db, `projects/${projectId}/teams`, teamId);
      await updateDoc(teamRef, {
        status: 'finished',
        finishedAt: serverTimestamp(),
        finishedBy: auth.currentUser?.uid,
        finishedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Unknown User'
      });

      // Refresh teams data for this project
      const teamsQuery = query(collection(db, `projects/${projectId}/teams`));
      const teamsSnap = await getDocs(teamsQuery);
      const updatedTeams = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setProjectTeams(prev => ({
        ...prev,
        [projectId]: updatedTeams
      }));

      console.log(`✅ Team ${teamId} marked as finished in project ${projectId}`);
    } catch (error) {
      console.error('Error marking team as finished:', error);
      alert('Failed to mark team as finished. Please try again.');
    }
  };

  // Function to close a project
  const handleCloseProject = async (project) => {
    if (!project) return;
    
    setClosingProject(true);
    try {
      const result = await projectClosureService.closeProject(project.id, {
        reason: 'Project completed by project manager'
      });

      if (result.success) {
        // Refresh the projects data to reflect the closure
        const user = auth.currentUser;
        if (user) {
          await fetchUserAndProjects(user);
        }
        
        setShowCloseProjectWarning(false);
        setProjectToClose(null);
        
        // Show success message
        alert(result.message);
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error closing project:', error);
      alert('Failed to close project. Please try again.');
    } finally {
      setClosingProject(false);
    }
  };

  const navStyle = {
    backgroundColor: 'var(--branding-sidebar-bg)',
    color: 'var(--cc-text-primary)',
    display: 'flex',
    flexDirection: 'column',
    width: '280px',
    height: '100vh',
    borderRight: '2px solid var(--branding-sidebar-border)',
    overscrollBehavior: 'none',
  };

  const linkStyle = (path) => ({
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: location.pathname === path ? 'var(--cc-active-bg)' : 'transparent',
    color: 'var(--cc-text-primary)',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'background 0.2s ease',
  });

  return (
    <div style={navStyle}>
      {/* Header Section - Fixed */}
      <div style={{ padding: '24px 24px 16px 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: sidebarLogoJustify, width: '100%', marginBottom: '40px' }}>
          <BrandedSidebarLogo
            src={sidebarLogoUrl || '/CrewChain%20Transparent.png'}
            alt={sidebarLogoUrl ? 'Company logo' : 'Crewchain'}
            isCustomCompanyLogo={Boolean(sidebarLogoUrl)}
            maxWidth={sidebarLogoDims.maxWidth}
            maxHeight={sidebarLogoDims.maxHeight}
            cornerRadius={sidebarLogoFrame.cornerRadius}
            backing={sidebarLogoFrame.backing}
            defaultLogoStyle={
              isDark && !sidebarLogoUrl
                ? { width: '200px', display: 'block', filter: 'invert(1)' }
                : { width: '200px', display: 'block' }
            }
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', margin: 0, color: 'var(--cc-text-secondary)' }}>
            Good {(() => { const h = new Date().getHours(); if (h < 12) return 'morning'; if (h < 18) return 'afternoon'; return 'evening'; })()},
          </p>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginTop: '4px', color: 'var(--cc-text-primary)' }}>
            {displayName}
          </h3>
        </div>

        <Link to="/home" style={{ ...linkStyle('/home'), textAlign: 'center', display: 'block' }}>
          Home
        </Link>

        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="cc-btn-primary"
          style={{ marginTop: '16px', width: '100%', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '14px' }}
        >
          + New project
        </button>
      </div>

      {/* Scrollable Content Section - Only My Projects */}
      <div
        style={{ flex: 1, overflowY: 'auto', padding: '0 24px', overscrollBehavior: 'none' }}
        onScroll={() => setActionPanel(null)}
      >
        {loading ? (
          <p style={{ color: 'var(--cc-text-muted)' }}>Loading projects...</p>
        ) : error ? (
          <div style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>
            {error}
          </div>
        ) : (
          <>

          {/* Your projects - Always show if user has created projects */}
          {yourProjects.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--cc-text-secondary)', marginBottom: '8px' }}>
                Your projects
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {yourProjects.map((project) => (
                  <div key={project.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Link
                      to={`/project/${project.id}`}
                      style={{
                        ...linkStyle(`/project/${project.id}`),
                        flex: 1
                      }}
                    >
                      {project.name || 'Unnamed project'}
                    </Link>
                    {project.status === 'active' && (
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActionPanel(prev =>
                              prev?.project.id === project.id ? null : { project, type: 'owned' }
                            );
                          }}
                          style={{
                            background: 'transparent',
                            color: 'var(--cc-text-muted)',
                            border: 'none',
                            borderRadius: 4,
                            padding: '4px',
                            fontSize: 16,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = 'var(--cc-text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = 'var(--cc-text-muted)';
                          }}
                          title="Project options"
                        >
                          ⋯
                        </button>

                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* Your collaborations - Show projects where user has been invited (both full and view-only) */}
          {collaborativeProjects.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--cc-text-secondary)', marginBottom: '8px' }}>
                Your collaborations
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {collaborativeProjects.map((project) => {
                  return (
                    <div key={project.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Link
                        to={`/project/${project.id}`}
                        style={{
                          ...linkStyle(`/project/${project.id}`),
                          flex: 1
                        }}
                      >
                        {project.name || 'Unnamed project'}
                      </Link>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActionPanel(prev =>
                              prev?.project.id === project.id ? null : { project, type: 'collab' }
                            );
                          }}
                          style={{
                            background: 'transparent',
                            color: 'var(--cc-text-muted)',
                            border: 'none',
                            borderRadius: 4,
                            padding: '4px',
                            fontSize: 16,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = 'var(--cc-text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = 'var(--cc-text-muted)';
                          }}
                          title="Project options"
                        >
                          ⋯
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Company projects - view-only, visible to company account owners across the whole company */}
          {companyProjects.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--cc-text-secondary)', marginBottom: '8px' }}>
                Company projects
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {companyProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/project/${project.id}`}
                    style={linkStyle(`/project/${project.id}`)}
                  >
                    {project.name || 'Unnamed project'}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Past Projects Section (Combined: Owned + Collaborated) */}
          {allPastProjects.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--cc-text-muted)', marginBottom: '8px' }}>
                Past projects
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {allPastProjects.map((project) => {
                  const isOwner = project.createdBy === auth.currentUser?.uid;
                  const userRole = getUserProjectRole(project, auth.currentUser?.uid);

                  return (
                    <div key={project.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Link
                        to={`/project/${project.id}`}
                        style={{
                          ...linkStyle(`/project/${project.id}`),
                          flex: 1,
                          color: 'var(--cc-text-muted)',
                          opacity: 0.8
                        }}
                      >
                        {project.name || 'Unnamed project'}
                      </Link>
                      {!isOwner && (
                        <span style={{
                          fontSize: '10px',
                          color: 'var(--cc-text-muted)',
                          background: 'var(--cc-badge-bg)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}>
                          {userRole === 'pm_collaborator' ? 'COLLAB' : userRole === 'pm_viewer' ? 'VIEWER' : 'TEAM'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <IpadSetupModal
            open={iPadSetupProject != null}
            project={iPadSetupProject}
            onClose={() => setIPadSetupProject(null)}
          />

          <NewProjectModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onCreate={() => {
              const user = auth.currentUser;
              if (user) fetchUserAndProjects(user);
            }}
          />

          <EditProjectModal
            isOpen={projectToEdit != null}
            project={projectToEdit}
            onClose={() => setProjectToEdit(null)}
            onSaved={() => {
              const user = auth.currentUser;
              if (user) fetchUserAndProjects(user);
            }}
          />


          {/* Close Project Warning Modal */}
          {showCloseProjectWarning && projectToClose && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: 'var(--cc-card-bg)',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '400px',
                width: '90vw',
                textAlign: 'center'
              }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef2f2', border: '2px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto' }}>
                  <AlertTriangle size={32} color="#dc2626" />
                </div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                  Close project?
                </h3>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--cc-text-muted)', lineHeight: '1.5' }}>
                  Are you sure you want to close <strong>"{projectToClose.name}"</strong>? This will make it read-only.
                </p>
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '10px 14px', marginBottom: '24px', fontSize: '13px', color: '#9a3412', textAlign: 'left', lineHeight: '1.5' }}>
                  Any tablet paired to this project will be automatically disconnected.
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => { setShowCloseProjectWarning(false); setProjectToClose(null); }}
                    style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}
                  >Cancel</button>
                  <button
                    onClick={() => handleCloseProject(projectToClose)}
                    disabled={closingProject}
                    style={{ background: closingProject ? '#9ca3af' : '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '500', cursor: closingProject ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: closingProject ? 0.7 : 1 }}
                  >{closingProject ? 'Closing...' : 'Close project'}</button>
                </div>
              </div>
            </div>
          )}

          {/* Finish Team Warning Modal */}
          {showFinishTeamWarning && teamToFinish && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{ background: 'var(--cc-card-bg)', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90vw', textAlign: 'center' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef2f2', border: '2px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto' }}>
                  <AlertTriangle size={32} color="#dc2626" />
                </div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                  Mark team as finished?
                </h3>
                <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--cc-text-muted)', lineHeight: '1.5' }}>
                  Are you sure you want to mark <strong>"{teamToFinish.teamName}"</strong> as finished? Team members will be notified and the team will be moved to the finished section.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => { setShowFinishTeamWarning(false); setTeamToFinish(null); }}
                    style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}
                  >Cancel</button>
                  <button
                    onClick={() => { handleMarkTeamFinished(teamToFinish.projectId, teamToFinish.teamId); setShowFinishTeamWarning(false); setTeamToFinish(null); }}
                    style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}
                  >Mark as finished</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>

      {/* Slide-in action panel */}
      {actionPanel && (() => {
        const { project, type } = actionPanel;
        const close = (e) => { if (e) e.stopPropagation(); setActionPanel(null); };
        const userRole = type === 'collab' ? getUserProjectRole(project, auth.currentUser?.uid) : null;
        const canEdit = type === 'owned' || userRole === 'pm_collaborator';
        const isOwner = project.createdBy === auth.currentUser?.uid;
        const teams = projectTeams[project.id] || [];

        const PanelBtn = ({ icon, label, onClick, danger, topBorder }) => (
          <button
            onClick={onClick}
            style={{
              width: '100%', background: 'transparent', border: 'none',
              borderTop: topBorder ? '1px solid var(--cc-subtle-border)' : 'none',
              padding: '11px 18px', fontSize: 13,
              color: danger ? '#ef4444' : 'var(--branding-app-primary)',
              cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: '10px',
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = danger ? '#fef2f2' : 'var(--branding-app-primary-soft)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {icon}{label}
          </button>
        );

        return (
          <>
            {/* Backdrop */}
            <div
              onClick={close}
              style={{ position: 'fixed', inset: 0, zIndex: 498, background: 'rgba(0,0,0,0.18)' }}
            />
            {/* Panel */}
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'fixed', left: 280, top: 0,
                width: 256, height: '100vh',
                background: 'var(--cc-card-bg)',
                borderLeft: '3px solid var(--branding-app-primary)',
                boxShadow: '6px 0 28px rgba(0,0,0,0.14)',
                zIndex: 499,
                display: 'flex', flexDirection: 'column',
                animation: 'ccPanelSlideIn 0.18s ease',
              }}
            >
              <style>{`@keyframes ccPanelSlideIn { from { transform: translateX(-12px); opacity: 0.5; } to { transform: translateX(0); opacity: 1; } }`}</style>

              {/* Panel header */}
              <div style={{ padding: '16px 18px 14px', borderBottom: '1px solid var(--cc-subtle-border)', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {project.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--cc-text-muted)', marginTop: 3, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {type === 'owned' ? 'Your project' : userRole === 'pm_collaborator' ? 'PM collaboration' : 'Collaboration'}
                    </div>
                  </div>
                  <button
                    onClick={close}
                    style={{ background: 'var(--cc-subtle-border)', border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 16, color: 'var(--cc-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >×</button>
                </div>
              </div>

              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: 'auto' }}>

                {/* Teams section — owned only */}
                {type === 'owned' && (
                  <div>
                    <div style={{ padding: '10px 18px 6px', fontSize: 10, fontWeight: 700, color: 'var(--cc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Teams
                    </div>
                    {teams.length > 0 ? teams.map(team => (
                      <div key={team.id} style={{ padding: '7px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--cc-subtle-border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: team.color, flexShrink: 0, display: 'inline-block' }} />
                          <span style={{ fontSize: 13, color: 'var(--cc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.name}</span>
                          {team.status === 'finished' && <span style={{ fontSize: 10, color: 'var(--cc-text-muted)', fontStyle: 'italic', flexShrink: 0 }}>finished</span>}
                        </div>
                        {team.status !== 'finished' && (
                          <button
                            onClick={e => { e.stopPropagation(); close(); setTeamToFinish({ projectId: project.id, teamId: team.id, teamName: team.name }); setShowFinishTeamWarning(true); }}
                            style={{ background: 'transparent', border: '1px solid #fecaca', padding: '2px 8px', fontSize: 11, color: '#dc2626', cursor: 'pointer', borderRadius: 4, flexShrink: 0 }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >Finish</button>
                        )}
                      </div>
                    )) : (
                      <div style={{ padding: '6px 18px 10px', fontSize: 12, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>No teams yet</div>
                    )}
                    <div style={{ height: 1, background: 'var(--cc-subtle-border)', margin: '6px 0' }} />
                  </div>
                )}

                {/* Actions */}
                <PanelBtn icon={<Shield size={14} />} label="Emergency" onClick={() => { close(); window.location.href = `/project/${project.id}/emergency`; }} />
                <PanelBtn icon={<FileText size={14} />} label="Support documents" onClick={() => { close(); window.location.href = `/project/${project.id}/support-documents`; }} />

                {canEdit && <>
                  <div style={{ height: 1, background: 'var(--cc-subtle-border)', margin: '4px 0' }} />
                  <PanelBtn icon={<Users size={14} />} label="Manage project team" onClick={() => { close(); window.location.href = `/project/${project.id}/team`; }} />
                  <PanelBtn icon={<Pencil size={14} />} label="Edit project details" onClick={() => { close(); setProjectToEdit(project); }} />
                  <PanelBtn icon={<Tablet size={14} />} label="Tablet setup" onClick={() => { close(); setIPadSetupProject(project); }} />
                </>}

                {type === 'owned' && isOwner && <>
                  <div style={{ height: 1, background: 'var(--cc-subtle-border)', margin: '4px 0' }} />
                  <PanelBtn danger label="Close project" onClick={() => { close(); setProjectToClose(project); setShowCloseProjectWarning(true); }} />
                </>}
              </div>
            </div>
          </>
        );
      })()}

      {/* Sign Out Button - Fixed at bottom */}
      <div style={{ padding: '24px', flexShrink: 0, borderTop: '1px solid var(--branding-app-surface-panel-border)' }}>
        <button
          onClick={async () => {
            try {
              await auth.signOut();
              // Clear any stored data
              localStorage.removeItem('companyId');
              // Clear sessionStorage to remove any cached routes and flags
              sessionStorage.clear();
              // Clear the hasVisitedHome flag specifically
              sessionStorage.removeItem('hasVisitedHome');
              // Navigate to login page and clear any location state
              // Use window.location to completely reset browser state
              window.location.href = '/';
            } catch (error) {
              console.error('Error signing out:', error);
              alert('Error signing out. Please try again.');
            }
          }}
          style={{
            width: '100%',
            backgroundColor: '#ef4444',
            color: '#fff',
            border: 'none',
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'background-color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#dc2626';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ef4444';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16,17 21,12 16,7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </div>
    </div>
  );
}
